import React, { useEffect, useState } from 'react';
import { Loader, Dimmer } from 'semantic-ui-react';
import { SelectableModalProps } from '../../../../../../components/types';
import FollowUp from '../../../../../../components/FollowUp';
import { getMessages } from '../../../../../../services/followup';
import MessageSender from '../../../../../../components/MessageSender';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { maskCpfOrCnpj } from '../../../../../../services/masks';
import { translatePropertyStatus } from '../../../../util';

const CLAIM_UPDATES_ACCENT_COLOR = '#59249B';

const InfoContainer = styled.div`
  width: 100%;
  background-color: #ffffff;
  border-left: 4px solid ${CLAIM_UPDATES_ACCENT_COLOR};
  padding: 0 0 0 12px;
  border-radius: 0;
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 0.7fr) minmax(0, 1.6fr);
  gap: 16px 24px;
  align-items: flex-start;
  color: #2f3441;
  margin: 0 0 22px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;

  > strong {
    color: #707682;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.15;
  }

  > span {
    color: #2f3441;
    font-size: 14px;
    line-height: 1.35;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
`;

const InfoSecondary = styled.span`
  color: #707682 !important;
  font-size: 12px !important;
  line-height: 1.25 !important;
`;

const SectionLabel = styled.div`
  color: #2f3441;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
`;

export type FollowUpModalProps = {
  id: string;
  claim: any;
  isBlocked?: boolean;
};

const FollowUpTab = (props: FollowUpModalProps) => {
  const { id, claim } = props; // , setUnread, unread
  const {
    propertyStreet,
    propertyNumber,
    propertyComplement,
    propertyDistrict,
    propertyCity,
    propertyState,
    tenantName,
    tenantDocument,
    estateName,
    createdAt,
    propertyStatus
  } = claim;
  const [messages, setMessages] = useState<any[]>([]);
  const [totalMessages, setTotalMessages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);

  const handleMessages = async () => {
    const response = await getMessages(id, page);
    const { data, count } = response;
    setMessages([...data]);
    setTotalMessages(count);
    setIsLoading(false);
  };

  useEffect(() => {
    handleMessages();
  }, [id]);

  const fullAddress = `${propertyStreet}, ${propertyNumber}${
    propertyComplement ? ` ${propertyComplement}` : ''
  } - ${propertyDistrict} - ${propertyCity} - ${propertyState}`;

  return (
    <>
      {isLoading ? (
        <Dimmer active={isLoading} inverted>
          <Loader content="Carregando mensagens..." />
        </Dimmer>
      ) : (
        <>
          <InfoContainer>
            <InfoItem>
              <strong>Inquilino</strong>
              <span>{tenantName}</span>
              {tenantDocument && (
                <InfoSecondary>{maskCpfOrCnpj(tenantDocument)}</InfoSecondary>
              )}
            </InfoItem>
            <InfoItem>
              <strong>Imobiliária</strong>
              <span>{estateName}</span>
            </InfoItem>
            <InfoItem>
              <strong>Data de abertura</strong>
              <span>{moment(createdAt).format('DD/MM/YYYY')}</span>
            </InfoItem>
            <InfoItem>
              <strong>Imóvel</strong>
              <span>{fullAddress}</span>
              {propertyStatus && (
                <InfoSecondary>
                  {translatePropertyStatus(propertyStatus)}
                </InfoSecondary>
              )}
            </InfoItem>
          </InfoContainer>
          <SectionLabel>Followup</SectionLabel>
          {messages.length > 0 ? (
            <FollowUp
              messages={messages}
              id={id}
              isBlocked={props.isBlocked}
              totalMessages={totalMessages}
              page={page}
              setPage={setPage}
              accentColor={CLAIM_UPDATES_ACCENT_COLOR}
              hideSendIcon
              showComposerDivider
              showPendingFields
              disableOwnScroll
            /> // setUnread={setUnread} unread={unread}
          ) : (
            <MessageSender
              id={id}
              isBlocked={props.isBlocked}
              accentColor={CLAIM_UPDATES_ACCENT_COLOR}
              hideSendIcon
              showComposerDivider
              showPendingFields
            />
          )}
        </>
      )}
    </>
  );
};

export default FollowUpTab;
